import { Button } from '@/components/ui/button';
import { UploadIcon, CheckCircle2 } from 'lucide-react';
import { useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useDropzone } from 'react-dropzone';

type FileUploadCardProps = {
  label: string;
  fileName?: string | null;
  onUpload: (file: File) => void;
  accept?: string;
  validated?: boolean;
  onClear?: () => void;
  enableDropzone?: boolean;
};

export function FileUploadCard({
  label,
  fileName,
  onUpload,
  accept,
  validated,
  onClear,
  enableDropzone = false,
}: FileUploadCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isFileValid = (file: File): boolean => {
    if (!accept) return true;
    return accept
      .split(',')
      .map((type) => type.trim())
      .some((type) => {
        if (type === 'audio/mp3') return file.type === 'audio/mpeg';
        if (type === 'image/*') return file.type.startsWith('image/');
        if (type === 'audio/*') return file.type.startsWith('audio/');
        if (type === 'video/mp4') return file.type === 'video/mp4';
        return file.type === type;
      });
  };

  const handleFile = (file: File) => {
    if (isFileValid(file)) {
      onUpload(file);
    } else {
      alert(`Please upload a valid ${label.toLowerCase()}.`);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleFile(acceptedFiles[0]);
      }
    },
    [handleFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    noClick: true, // We'll handle clicks manually
    noKeyboard: true,
    disabled: !enableDropzone,
  });

  return (
    <div
      {...(enableDropzone ? getRootProps() : {})}
      onClick={openFilePicker}
      className={clsx(
        'w-full border-2 rounded-md p-4 flex flex-col gap-3 relative transition-all cursor-pointer',
        validated ? 'border-green-500' : 'border-muted border',
        enableDropzone && 'border-dashed hover:border-primary',
        isDragActive && 'bg-muted'
      )}
    >
      <label className="text-sm font-medium flex items-center justify-between pointer-events-none">
        {label}
        {validated && <CheckCircle2 className="text-green-500 w-4 h-4" />}
      </label>

      <div className="w-full px-4 py-2 border border-input rounded-md bg-card text-sm pointer-events-none">
        {fileName
          ? `"${fileName}" Selected`
          : enableDropzone
          ? 'Drag a file here or click to upload'
          : 'No file selected'}
      </div>

      <input
        {...getInputProps()}
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />

      <Button
        onClick={(e) => {
          e.stopPropagation(); // Prevent double-triggering
          openFilePicker();
        }}
        variant="secondary"
        className="w-full"
        type="button"
      >
        <UploadIcon className="w-4 h-4 mr-2" />
        Upload {label}
      </Button>
    </div>
  );
}